interface AppEnvironment {
    ROLLBAR_ACCESS_TOKEN?: string;
    NAME?: string;
    VERSION?: string;
}

interface RollbarFrame {
    filename?: string;
}

interface RollbarException {
    message?: string;
}

interface RollbarTrace {
    frames?: RollbarFrame[];
    exception?: RollbarException;
}

interface RollbarPayload {
    body?: {
        trace?: RollbarTrace;
        trace_chain?: RollbarTrace[];
        message?: { body?: string };
    };
}

export interface RollbarConfig {
    accessToken: string | undefined;
    environment: string | undefined;
    enabled: boolean;
    captureUncaught: boolean;
    captureUnhandledRejections: boolean;
    maxItems: number;
    itemsPerMinute: number;
    checkIgnore: (isUncaught: boolean, args: unknown[], payload: RollbarPayload) => boolean;
    payload: {
        client: {
            javascript: {
                code_version: string | undefined;
            };
        };
    };
}

// Per-tab caps under Rollbar's server-side ceiling; maxItems is per-page-load lifetime, itemsPerMinute the rate guard.
const MAX_ITEMS_PER_PAGE_LOAD = 50;
const ITEMS_PER_MINUTE = 10;

// Non-actionable noise that has flooded Rollbar.
const NOISE_MESSAGE_PATTERNS: RegExp[] = [
    /ResizeObserver loop/i,
    /^Script error\.?$/i,
    // info-free only; keep rejections with a real reason
    /^Non-Error promise rejection captured(?: with value:\s*(?:undefined|null|\{\}|\[object Object\])?\s*)?$/i,
    // Chromium DecompressionStream errors from the pdf.js worker; pdf.js falls back to its JS inflater
    /^(?:Uncaught )?(?:TypeError: )?Compressed input was truncated\.?$/i,
    /^(?:Uncaught )?(?:TypeError: )?The compressed data was not valid: .+$/i,
    // a reading/writing-aid extension's own messaging (isPredictionAvailable, dictateStateDistributor.getValue...)
    /^"?Request timeout [\w.]+"?$/,
];

const EXTENSION_FILENAME_PATTERN = /^(chrome|moz|safari|webkit|ms-browser)-extension:/i;

// backend business warnings (e.g. 311 task already handled) rejected as the bare response body; 5xx stays reported
const isApiWarning = (arg: unknown): boolean => {
    if (typeof arg !== 'object' || arg === null) return false;
    const {errorType, httpStatus} = arg as {errorType?: unknown; httpStatus?: unknown};
    return errorType === 'WARNING' && !(typeof httpStatus === 'number' && httpStatus >= 500);
};

const extractMessage = (args: unknown[], payload: RollbarPayload): string => {
    const first = args[0];
    if (typeof first === 'string') return first;
    if (first instanceof Error) return first.message;
    // thrown exceptions carry the message under trace, not message.body
    const exception = payload.body?.trace?.exception ?? payload.body?.trace_chain?.[0]?.exception;
    return exception?.message ?? payload.body?.message?.body ?? '';
};

const extractFrames = (payload: RollbarPayload): RollbarFrame[] => {
    const direct = payload.body?.trace?.frames ?? [];
    const chained = payload.body?.trace_chain?.flatMap(t => t.frames ?? []) ?? [];
    return [...direct, ...chained];
};

export const checkIgnore = (
    _isUncaught: boolean,
    args: unknown[],
    payload: RollbarPayload,
): boolean => {
    const message = extractMessage(args, payload);
    if (NOISE_MESSAGE_PATTERNS.some(pattern => pattern.test(message))) return true;
    if (args.some(isApiWarning)) return true;

    const frames = extractFrames(payload);
    if (frames.some(frame => EXTENSION_FILENAME_PATTERN.test(frame.filename ?? ''))) return true;

    return false;
};

export const buildRollbarConfig = (environment: AppEnvironment | undefined): RollbarConfig => ({
    accessToken: environment?.ROLLBAR_ACCESS_TOKEN,
    environment: environment?.NAME,
    enabled: Boolean(environment?.ROLLBAR_ACCESS_TOKEN) && environment?.NAME !== 'local',
    captureUncaught: true,
    captureUnhandledRejections: true,
    maxItems: MAX_ITEMS_PER_PAGE_LOAD,
    itemsPerMinute: ITEMS_PER_MINUTE,
    checkIgnore,
    payload: {
        client: {
            javascript: {
                code_version: environment?.VERSION,
            },
        },
    },
});